/**
 * Os bancos IndexedDB que o app usava antes da família `BF_SELLER_*`.
 *
 * A renomeação dos bancos não apagou nada do aparelho — deixou tudo **invisível**: o app novo abre
 * outro nome de banco e encontra tudo vazio. Para o RCA em campo isso é carrinho montado perdido,
 * pedido offline não enviado perdido e o catálogo de imagens baixado de novo pela rede móvel.
 *
 * | Domínio    | Banco antigo             | Banco atual            |
 * | ---------- | ------------------------ | ---------------------- |
 * | datasets   | `BATE_FORTE`             | `BF_SELLER_DATASETS`   |
 * | eventos    | `BATE_FORTE`             | `BF_SELLER_EVENTS`     |
 * | imagens    | `BATE_FORTE_IMAGES`      | `BF_SELLER_IMAGES`     |
 * | carrinhos  | `PWA_RCA_CARTS`          | `BF_SELLER_CARTS`      |
 * | pedidos    | `pwa_rca_orders`         | `BF_SELLER_ORDERS`     |
 *
 * `toCoded` é aplicado nos nomes que o app antigo também codificava (os dois `BATE_FORTE*`, abertos
 * pelo `useDBFunctions`) e **não** nos que ele escrevia literais (os dois do `idb-keyval`). Errar
 * esse par é abrir um banco que não existe e concluir que não há nada a migrar.
 */

import { deleteDB, openDB, type IDBPDatabase } from 'idb';
import { toCoded } from '@/shared/coded';

/** Datasets do catálogo + a store `offline_events`. Os datasets voltam pelo sync; ver a migração. */
export const LEGACY_DATASETS_DB = toCoded('BATE_FORTE');

/** Imagens de produto em base64, uma linha por `COD_PRODUTO`. */
export const LEGACY_IMAGES_DB = toCoded('BATE_FORTE_IMAGES');
export const LEGACY_IMAGES_STORE = toCoded('imagens');

/** Carrinhos do `idb-keyval`: linhas `cart:<escopo>` e, em versões mais antigas, o blob `cart_details`. */
export const LEGACY_CARTS_DB = 'PWA_RCA_CARTS';
export const LEGACY_CARTS_STORE = 'cart_details';

/** Fila de pedidos do `idb-keyval`: linhas `order:<nro_pedido>`. */
export const LEGACY_ORDERS_DB = 'pwa_rca_orders';
export const LEGACY_ORDERS_STORE = 'orders';

/** Todos os bancos legados, na ordem em que faz sentido apagá-los (o mais pesado primeiro). */
export const LEGACY_DB_NAMES = [LEGACY_DATASETS_DB, LEGACY_IMAGES_DB, LEGACY_CARTS_DB, LEGACY_ORDERS_DB] as const;

/**
 * Abre um banco legado **sem criá-lo**.
 *
 * `indexedDB.open(nome)` sem versão cria o banco quando ele não existe. Num aparelho que nunca
 * rodou a versão antiga isso deixaria para trás justamente os bancos vazios que a migração
 * promete não deixar. `oldVersion === 0` no `upgrade` é o sinal de que o banco nasceu neste
 * `open`: fecha e apaga em seguida.
 *
 * Sem versão também é a única forma segura de abrir: a versão gravada no disco vinha do
 * `package.json` e sobe sozinha entre releases — pedir uma versão menor lança `VersionError`.
 */
export const openLegacyDbIfExists = async (name: string): Promise<IDBPDatabase | null> => {
  let nasceuAgora = false;

  let db: IDBPDatabase;
  try {
    db = await openDB(name, undefined, {
      upgrade(_db, oldVersion) {
        if (oldVersion === 0) nasceuAgora = true;
      },
      blocked() {
        console.warn(`[legacyIdb] abertura de ${name} bloqueada por outra aba`);
      },
    });
  } catch (error) {
    console.info(`[legacyIdb] não foi possível abrir ${name}:`, error);
    return null;
  }

  if (!nasceuAgora) return db;

  db.close();
  await dropLegacyDb(name);
  return null;
};

export const dropLegacyDb = async (name: string): Promise<boolean> => {
  try {
    await deleteDB(name, {
      blocked() {
        console.warn(`[legacyIdb] exclusão de ${name} aguardando outras conexões fecharem`);
      },
    });
    return true;
  } catch (error) {
    console.error(`[legacyIdb] falha ao excluir ${name}:`, error);
    return false;
  }
};

/**
 * Lê uma store inteira de um banco no formato do `idb-keyval` (chave fora da linha, valor string).
 *
 * Carrinhos e pedidos legados têm o mesmo formato, então o leitor é um só. Devolve `{}` quando o
 * banco ou a store não existem — nada a migrar é o caso comum, não um erro.
 */
export const readLegacyKeyvalStore = async (dbName: string, storeName: string): Promise<Record<string, string>> => {
  const db = await openLegacyDbIfExists(dbName);
  if (!db) return {};

  try {
    if (!db.objectStoreNames.contains(storeName)) return {};

    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const [keys, values] = await Promise.all([store.getAllKeys(), store.getAll()]);
    await tx.done;

    const out: Record<string, string> = {};
    keys.forEach((key, index) => {
      const value = values[index];
      if (typeof key !== 'string' || typeof value !== 'string') return;
      out[key] = value;
    });
    return out;
  } catch (error) {
    console.error(`[legacyIdb] falha ao ler ${dbName}/${storeName}:`, error);
    return {};
  } finally {
    db.close();
  }
};
